import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Loader from "../../components/Global/Loader";
import BoardManagementModal from "../../components/Admin/CRM/BoardManagementModal";
import { UserContext } from "../../context/UserContext";
import { fetchKanbanBoards, deleteKanbanBoard } from "../../services/crmApi";
import {
  fetchSalesPersonsNormalized,
  salesPersonOptionLabel,
} from "../../services/salesPersonApi";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiArrowRight,
  FiLayers,
  FiUsers,
  FiCalendar,
  FiSearch,
  FiUser,
  FiX,
  FiUserCheck,
  FiBarChart2,
} from "react-icons/fi";

/** Sales persons assigned on the board document (API: `salesPerson` array). */
const normalizeSalesPersonsOnBoard = (board) => {
  const raw = board?.salesPerson ?? board?.salesperson ?? board?.salesPersons;
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : [raw];
  return list
    .filter(Boolean)
    .map((sp) => ({
      id: String(sp._id || sp.id || sp.email || ""),
      name: sp.name || sp.email || "Sales person",
      email: sp.email || "",
    }))
    .filter((sp) => sp.id);
};

/** SPs who have this board id in `assignedBoardIds` (admin registry). */
const salesPersonsForBoardFromRegistry = (boardId, registry) => {
  if (!boardId || !Array.isArray(registry)) return [];
  const bid = String(boardId);
  return registry
    .filter((sp) => {
      const ids = (sp.assignedBoardIds || []).map((b) =>
        typeof b === "object" ? String(b._id || b.id || "") : String(b),
      );
      return ids.includes(bid);
    })
    .map((sp) => ({
      id: String(sp._id || sp.id || ""),
      name: sp.name || sp.email || "Sales person",
      email: sp.email || "",
    }))
    .filter((sp) => sp.id);
};

const CRM = () => {
  const navigate = useNavigate();
  const { userRole } = useContext(UserContext);
  const isAdmin = userRole?.toLowerCase() === "admin";
  const isConsultant = userRole?.toLowerCase() === "consultant";
  const basePath = isAdmin ? "/admin/crm" : isConsultant ? "/consultant/crm" : "/sales/crm";
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showBoardModal, setShowBoardModal] = useState(false);
  const [editingBoard, setEditingBoard] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [salesPersons, setSalesPersons] = useState([]);
  const [selectedSpFilter, setSelectedSpFilter] = useState("");

  // Debounce the search input so we don't fire a request on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const loadBoards = useCallback(async (spId = "", search = "") => {
    try {
      setLoading(true);
      setError(null);
      const opts = {};
      if (spId) opts.salesPerson = spId;
      if (search) opts.search = search;
      const response = await fetchKanbanBoards(opts);
      setBoards(response?.data || []);
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Failed to load boards",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + refetch from backend whenever the SP filter or search changes.
  // Search runs server-side for both admin and sales person; SP filter is admin-only.
  useEffect(() => {
    loadBoards(isAdmin ? selectedSpFilter : "", debouncedSearch);
  }, [loadBoards, isAdmin, selectedSpFilter, debouncedSearch]);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const normalized = await fetchSalesPersonsNormalized();
        setSalesPersons(normalized);
      } catch (_) {
        setSalesPersons([]);
      }
    })();
  }, [isAdmin]);

  const handleCreateBoard = () => {
    setEditingBoard(null);
    setShowBoardModal(true);
  };

  const handleEditBoard = (board) => {
    setEditingBoard(board);
    setShowBoardModal(true);
  };

  const handleDeleteBoard = async (boardId) => {
    try {
      setActionLoading(true);
      await deleteKanbanBoard(boardId);
      toast.success("Board deleted successfully");
      setDeleteConfirm(null);
      await loadBoards(isAdmin ? selectedSpFilter : "", debouncedSearch);
    } catch (err) {
      // toast handled by interceptor
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenBoard = (boardId) => {
    navigate(`${basePath}/${boardId}`);
  };

  const handleBoardSaved = async () => {
    setShowBoardModal(false);
    setEditingBoard(null);
    await loadBoards(isAdmin ? selectedSpFilter : "", debouncedSearch);
  };

  const selectedSpName = useMemo(() => {
    if (!selectedSpFilter) return "";
    const sp = salesPersons.find((s) => (s._id || s.id) === selectedSpFilter);
    return sp?.name || sp?.email || "";
  }, [selectedSpFilter, salesPersons]);

  // Compute stages and leads counts from populated columns. For admin, also
  // aggregate per-sales-person lead counts so we can render them on the card.
  const enrichedBoards = useMemo(() => {
    return boards.map((b) => {
      const stagesCount = Array.isArray(b.columns) ? b.columns.length : 0;
      const allLeads = Array.isArray(b.columns)
        ? b.columns.flatMap((col) =>
            Array.isArray(col?.leads) ? col.leads : [],
          )
        : [];
      const leadsCount = allLeads.length;

      // Build a map of sales-person id -> { name, count } so duplicate SPs
      // across columns get merged into a single row, sorted by count desc.
      const spMap = new Map();
      let unassignedCount = 0;
      allLeads.forEach((lead) => {
        const sp = lead?.salesPerson;
        if (sp && typeof sp === "object") {
          const id = sp._id || sp.id || sp.email || "unknown";
          if (!spMap.has(id)) {
            spMap.set(id, {
              id,
              name: sp.name || sp.email || "Unknown",
              count: 0,
            });
          }
          spMap.get(id).count += 1;
        } else if (typeof sp === "string" && sp) {
          if (!spMap.has(sp)) spMap.set(sp, { id: sp, name: sp, count: 0 });
          spMap.get(sp).count += 1;
        } else {
          unassignedCount += 1;
        }
      });
      const salesPersonBreakdown = Array.from(spMap.values()).sort(
        (a, b) => b.count - a.count,
      );

      return {
        ...b,
        stagesCount,
        leadsCount,
        salesPersonBreakdown,
        unassignedCount,
      };
    });
  }, [boards]);

  // Search & SP filter both run on the backend now — render the result as-is.
  const filteredBoards = enrichedBoards;

  if (loading) return <Loader />;

  return (
    <div className="pl-36 mt-16 min-h-screen bg-[#f4f8fb] p-8">
      {actionLoading && <Loader />}

      {/* Header Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">CRM</h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage student leads through kanban boards
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={handleCreateBoard}
              className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-md flex items-center gap-2 transition-colors shadow-sm"
            >
              <FiPlus size={18} />
              Create Board
            </button>
          )}
        </div>

        <div className="mt-6 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <FiSearch
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search boards by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 transition shadow-sm"
              style={{
                borderBottomWidth: "3px",
                borderBottomColor: "#3b82f6",
              }}
            />
          </div>
          {isAdmin && (
            <div className="relative md:w-72">
              <FiUser
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={16}
              />
              <select
                value={selectedSpFilter}
                onChange={(e) => setSelectedSpFilter(e.target.value)}
                className="w-full pl-9 pr-8 py-2 text-sm text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 transition shadow-sm bg-white appearance-none"
                style={{
                  borderBottomWidth: "3px",
                  borderBottomColor: "#3b82f6",
                }}
              >
                <option value="">Filter by sales person (all)</option>
                {salesPersons.map((sp) => (
                  <option key={sp._id || sp.id} value={sp._id || sp.id}>
                    {salesPersonOptionLabel(sp)}
                  </option>
                ))}
              </select>
              {selectedSpFilter && (
                <button
                  type="button"
                  onClick={() => setSelectedSpFilter("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear filter"
                >
                  <FiX size={14} />
                </button>
              )}
            </div>
          )}
        </div>
        {isAdmin && selectedSpFilter && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
            <FiUser size={12} />
            Showing boards for: {selectedSpName || selectedSpFilter}
            <button
              type="button"
              onClick={() => setSelectedSpFilter("")}
              className="ml-1 hover:text-blue-900"
            >
              <FiX size={12} />
            </button>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 flex justify-between items-center">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-700 underline text-sm"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Boards Grid */}
      {filteredBoards.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
            <FiLayers size={28} className="text-blue-600" />
          </div>
          <p className="text-gray-700 text-lg font-medium mb-2">
            {debouncedSearch
              ? "No boards match your search"
              : isAdmin
                ? selectedSpFilter
                  ? "No boards assigned to this sales person"
                  : "No boards created yet"
                : "No boards assigned to you"}
          </p>
          <p className="text-gray-500 text-sm mb-6">
            {debouncedSearch
              ? "Try a different search term or clear the search."
              : isAdmin
                ? selectedSpFilter
                  ? "Try clearing the sales person filter or assigning boards to them."
                  : "Create your first Kanban board to start managing leads"
                : "Ask your admin to assign you a board to get started"}
          </p>
          {!debouncedSearch && !selectedSpFilter && isAdmin && (
            <button
              onClick={handleCreateBoard}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md inline-flex items-center gap-2 transition-colors"
            >
              <FiPlus size={20} />
              Create Your First Board
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBoards.map((board) => (
            <div
              key={board._id}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow flex flex-col"
            >
              {/* Card Header */}
              <button
                type="button"
                onClick={() => handleOpenBoard(board._id)}
                className="text-left p-6 border-b border-gray-100 hover:bg-blue-50 transition-colors rounded-t-lg"
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-1 truncate">
                  {board.name}
                </h3>
                <p className="text-sm text-gray-500 line-clamp-2 min-h-[2.5rem]">
                  {board.description || "No description provided"}
                </p>
              </button>

              {/* Card Body */}
              <div className="px-6 pt-6 pb-3 grid grid-cols-3 gap-2 text-center text-sm">
                <div className="flex flex-col items-center">
                  <FiLayers className="text-blue-600 mb-1" size={18} />
                  <span className="font-semibold text-gray-800">
                    {board.stagesCount}
                  </span>
                  <span className="text-xs text-gray-500">Stages</span>
                </div>
                <div className="flex flex-col items-center">
                  <FiUsers className="text-blue-600 mb-1" size={18} />
                  <span className="font-semibold text-gray-800">
                    {board.leadsCount}
                  </span>
                  <span className="text-xs text-gray-500">Leads</span>
                </div>
                <div className="flex flex-col items-center">
                  <FiCalendar className="text-blue-600 mb-1" size={18} />
                  <span className="font-semibold text-gray-800 text-xs">
                    {board.createdAt
                      ? new Date(board.createdAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })
                      : "N/A"}
                  </span>
                  <span className="text-xs text-gray-500">Created</span>
                </div>
              </div>
              {/* Card Footer */}
              <div className="p-4 border-t border-gray-100 flex gap-2 flex-wrap">
                <button
                  onClick={() => handleOpenBoard(board._id)}
                  className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 px-4 py-2 rounded-md font-medium flex items-center justify-center gap-2 transition-colors text-sm min-w-[120px]"
                >
                  Open Board <FiArrowRight size={14} />
                </button>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`${basePath}/${board._id}/dashboard`, {
                        state: { fromCRM: true },
                      })
                    }
                    className="flex-1 bg-teal-50 hover:bg-teal-100 text-teal-800 px-4 py-2 rounded-md font-medium flex items-center justify-center gap-2 transition-colors text-sm min-w-[120px]"
                    title="CRM dashboard"
                  >
                    <FiBarChart2 size={14} />
                    Dashboard
                  </button>
                )}
                {isAdmin && (
                  <>
                    <button
                      onClick={() => handleEditBoard(board)}
                      className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md transition-colors"
                      title="Edit board"
                    >
                      <FiEdit2 size={16} />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(board)}
                      className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-md transition-colors"
                      title="Delete board"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Board Management Modal */}
      {showBoardModal && (
        <BoardManagementModal
          board={editingBoard}
          onClose={() => {
            setShowBoardModal(false);
            setEditingBoard(null);
          }}
          onSave={handleBoardSaved}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Delete Board
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete{" "}
              <span className="font-semibold">"{deleteConfirm.name}"</span>?
              This will permanently remove all its stages, leads, and notes.
              This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteBoard(deleteConfirm._id)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRM;
